import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const PWAContext = createContext(null);

export function PWAProvider({ children }) {
  const [deferred, setDeferred] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    setIsStandalone(standalone);
    if (standalone) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setDeferred(null);
      setIsStandalone(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return { ok: false, reason: 'unavailable' };
    deferred.prompt();
    try {
      const choice = await deferred.userChoice;
      setDeferred(null);
      return { ok: choice?.outcome === 'accepted', outcome: choice?.outcome };
    } catch (e) {
      return { ok: false, error: String(e) };
    }
  }, [deferred]);

  return (
    <PWAContext.Provider value={{ canInstall: !!deferred, isStandalone, install }}>
      {children}
    </PWAContext.Provider>
  );
}

export function usePWA() {
  const ctx = useContext(PWAContext);
  if (!ctx) return { canInstall: false, isStandalone: false, install: async () => ({ ok: false }) };
  return ctx;
}
